import { HistoryManager } from './history.js';

// Initial check to prevent errors if not used yet, but we will use it
// The state only tracks the *structure* of the PDF (pages list), not the heavy PDF objects.
// docId -> { file, pdfBytes (optional), pageCount }
// The actual PDF manipulation happens via the Engine/Worker which holds the "real" docs.
// But we need to map pageId -> sourceDocId

class Store {
    constructor() {
        this.state = {
            documents: {}, // id -> metadata
            pages: [],     // { id, docId, pageIndex, rotation }
            selection: [], // Array of pageIds
        };

        this.history = new HistoryManager(this.getSnapshot());
        this.listeners = new Set();
        this.counter = 0; // For unique page IDs
    }

    // -- State Access --
    getSnapshot() {
        // We only snapshot pages for undo/redo. Selection might not need undo history? 
        // Usually undoing page deletion is important. Undoing selection is less critical but nice.
        // Let's snapshot { pages } for now. Documents usually merely accumulate.
        return {
            pages: JSON.parse(JSON.stringify(this.state.pages))
        };
    }

    getState() {
        return this.state;
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    notify(eventType, payload) {
        this.listeners.forEach(listener => listener(this.state, eventType, payload));
    }

    // -- Actions --

    // Called when a new PDF is loaded from disk
    addDocument(docId, pageCount, fileName) {
        this.state.documents[docId] = {
            id: docId,
            pageCount,
            fileName
        };
        // Note: We don't push to history for just loading a doc, unless it modifies the page list immediately.
        // Usually "Opening" a PDF replaces the current state or appends?
        // Let's assume "Open" replaces (clears old), "Import" appends.
    }

    setPages(newPages, isNewHistoryEntry = true) {
        if (isNewHistoryEntry) {
            this.history.push(this.getSnapshot());
        }
        this.state.pages = newPages;
        this.notify('PAGES_CHANGED');
    }

    // Initialize with a single PDF's pages
    initPagesFromDoc(docId, pageCount) {
        // Reset
        this.state.pages = [];
        this.state.selection = [];
        this.history = new HistoryManager({ pages: [] }); // Reset history

        const newPages = [];
        for (let i = 0; i < pageCount; i++) {
            newPages.push({
                id: `p_${this.counter++}`,
                docId: docId,
                pageIndex: i,
                rotation: 0
            });
        }
        this.state.pages = newPages;

        // Push initial state to history? No, initial state is baseline.
        this.history = new HistoryManager(this.getSnapshot());

        this.notify('PAGES_CHANGED');
        this.notify('SELECTION_CHANGED');
    }

    // Append pages from an imported doc
    appendPagesFromDoc(docId, pageCount) {
        this.history.push(this.getSnapshot());

        const newPages = [];
        for (let i = 0; i < pageCount; i++) {
            newPages.push({
                id: `p_${this.counter++}`,
                docId: docId,
                pageIndex: i,
                rotation: 0
            });
        }
        this.state.pages = [...this.state.pages, ...newPages];
        this.notify('PAGES_CHANGED');
    }

    selectPage(pageId, multiSelect = false) {
        if (!multiSelect) {
            this.state.selection = [pageId];
        } else {
            if (this.state.selection.includes(pageId)) {
                this.state.selection = this.state.selection.filter(id => id !== pageId);
            } else {
                this.state.selection.push(pageId);
            }
        }
        this.notify('SELECTION_CHANGED');
    }

    clearSelection() {
        this.state.selection = [];
        this.notify('SELECTION_CHANGED');
    }

    movePage(fromIndex, toIndex) {
        if (fromIndex === toIndex) return;

        this.history.push(this.getSnapshot());

        const pages = [...this.state.pages];
        const [movedPage] = pages.splice(fromIndex, 1);
        pages.splice(toIndex, 0, movedPage);

        this.state.pages = pages;
        this.notify('PAGES_CHANGED');
    }

    deleteSelectedPages() {
        if (this.state.selection.length === 0) return;

        this.history.push(this.getSnapshot());

        this.state.pages = this.state.pages.filter(p => !this.state.selection.includes(p.id));
        this.state.selection = [];

        this.notify('PAGES_CHANGED');
        this.notify('SELECTION_CHANGED');
    }

    rotateSelectedPages() {
        if (this.state.selection.length === 0) return;

        this.history.push(this.getSnapshot());

        this.state.pages = this.state.pages.map(p => {
            if (this.state.selection.includes(p.id)) {
                return { ...p, rotation: (p.rotation + 90) % 360 };
            }
            return p;
        });

        this.notify('PAGES_CHANGED'); // Rotation updates visual
    }

    undo() {
        const prevState = this.history.undo();
        if (prevState) {
            this.state.pages = prevState.pages;
            // Clear selection on undo to avoid ghost selections
            this.state.selection = [];

            this.notify('PAGES_CHANGED');
            this.notify('SELECTION_CHANGED');
            this.notify('HISTORY_CHANGED');
        }
    }

    redo() {
        const nextState = this.history.redo();
        if (nextState) {
            this.state.pages = nextState.pages;
            this.state.selection = [];

            this.notify('PAGES_CHANGED');
            this.notify('SELECTION_CHANGED');
            this.notify('HISTORY_CHANGED');
        }
    }

    get canUndo() { return this.history.canUndo(); }
    get canRedo() { return this.history.canRedo(); }
}

export const appStore = new Store();
